import { Box, Stack, styled } from '@mui/material';

import { Button, CHANGED_FIELD_COLOR_VAR, Tabs, theme as ds } from '@/design-system';
import { forMobile } from '@/styles/responsive';

export const VersionTabs = styled(Tabs.Root)({
  width: '100%',

  '& > div': {
    width: '100%',
  },

  '& [role="tablist"]': {
    display: 'flex',
    width: '100%',
  },

  '& [role="tab"]': {
    flex: '1 0 0',
    minWidth: 0,
    justifyContent: 'center',
  },
});

export const ReviewReadOnly = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$highlightColor',
})<{ $highlightColor: string }>(({ $highlightColor }) => ({
  [CHANGED_FIELD_COLOR_VAR]: $highlightColor,
}));

export const SectionHeading = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: `${ds.spacing[8]}px`,
});

export const StatusBadgeRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: `${ds.spacing[8]}px`,
});

export const StatusBadgeTrigger = styled(Box)({
  display: 'inline-flex',
});

export const WizardShell = styled(Stack)({
  width: '100%',
  flexDirection: 'column',
  gap: `${ds.spacing[24]}px`,
});

export const LinksContainer = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: `${ds.spacing[16]}px`,
  width: '100%',
  boxSizing: 'border-box',
  padding: `${ds.spacing[16]}px`,
  borderRadius: `${ds.radius[16]}px`,
  border: `1px solid ${ds.colors.border.default}`,
  background: ds.colors.background.raised,
});

// No Button variant maps to an overlay surface on a raised modal: subtle/tertiary
// resolves to neutral.subtle, which is darker than the modal itself.
export const ModalOptionButton = styled(Button)({
  justifyContent: 'space-between',
  padding: `${ds.spacing[16]}px`,
  backgroundColor: ds.colors.background.overlay,

  '&:hover': {
    backgroundColor: ds.colors.neutral.hover,
  },

  svg: {
    width: 16,
    height: 16,
  },
});

// Mirrors the design-system FileUpload row, whose Figma instance this card renders.
export const DirectorHeadshot = styled('img')({
  width: 32,
  height: 32,
  flexShrink: 0,
  borderRadius: `${ds.radius.full}px`,
  objectFit: 'cover',
  objectPosition: 'center',
});

export const VerificationPanel = styled(Stack, {
  shouldForwardProp: (prop) => prop !== '$invalid' && prop !== '$changed',
})<{ $invalid: boolean; $changed?: string }>(({ $invalid, $changed }) => ({
  flexDirection: 'column',
  gap: `${ds.spacing[16]}px`,
  padding: `${ds.spacing[24]}px`,
  borderRadius: `${ds.radius[16]}px`,
  border: `1px solid ${$invalid ? ds.colors.error.border : ($changed ?? 'transparent')}`,
  backgroundColor: ds.colors.background.raised,
}));

export const ReviewField = styled(Stack)({
  flexDirection: 'column',
  gap: `${ds.spacing[8]}px`,
  minWidth: 0,
});

export const ReviewRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr)) 184px 184px',
  gap: `${ds.spacing[16]}px`,
  width: '100%',

  [forMobile(theme)]: {
    gridTemplateColumns: '1fr',
  },
}));

export const ReviewRowLabel = styled(Box)(({ theme }) => ({
  gridColumn: 'span 3',
  display: 'flex',
  alignItems: 'center',

  [forMobile(theme)]: {
    gridColumn: 'auto',
  },
}));

export const PanelHeader = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: `${ds.spacing[8]}px`,
  width: '100%',
});

export const StepSection = styled(Stack, {
  shouldForwardProp: (prop) => prop !== '$changed',
})<{ $changed?: string }>(({ $changed }) => ({
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: `${ds.spacing[16]}px`,
  width: '100%',
  ...($changed && {
    padding: `${ds.spacing[24]}px`,
    borderRadius: `${ds.radius[16]}px`,
    border: `1px solid ${$changed}`,
  }),
}));

export const TitleRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: `${ds.spacing[16]}px`,
  width: '100%',
});

export const FormPanel = styled(Stack, {
  shouldForwardProp: (prop) => prop !== '$changed',
})<{ $changed?: string }>(({ $changed }) => ({
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: `${ds.spacing[16]}px`,
  width: '100%',
  padding: `${ds.spacing[24]}px`,
  borderRadius: `${ds.radius[16]}px`,
  background: ds.colors.background.raised,
  border: `1px solid ${$changed ?? 'transparent'}`,
}));

export const LogoUploadColumn = styled(Stack)({
  flexDirection: 'column',
  gap: `${ds.spacing[8]}px`,
  width: '100%',
  maxWidth: 544,
});

export const FieldGrid = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$columns',
})<{ $columns: number }>(({ $columns }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${$columns}, minmax(0, 1fr))`,
  gridAutoRows: 'minmax(min-content, max-content)',
  gap: `${ds.spacing[16]}px`,
  width: '100%',
}));

export const FooterActions = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});
